import { useL } from "@/lib/useL";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Headphones, Repeat, SkipBack, SkipForward, Square, Play } from "lucide-react";
import { InfoTip } from "@/components/InfoTip";

const HEADING = /^#{1,4}\s+/;

// Title + stanzas of the poem (stops at the next heading, e.g. "Ce qu'il faut retenir").
export function parsePoem(md) {
  const lines = String(md || "").split(/\r?\n/);
  const start = lines.findIndex((l) => HEADING.test(l.trim()));
  const body = [];
  for (let j = start + 1; j < lines.length; j++) {
    if (HEADING.test(lines[j].trim())) break;
    body.push(lines[j].replace(/[*_]/g, "").trim());
  }
  const stanzas = body.join("\n").split(/\n\s*\n/).map((s) => s.split("\n").filter(Boolean)).filter((s) => s.length);
  return { title: start >= 0 ? lines[start].replace(HEADING, "").trim() : "", stanzas };
}

const VOICES = { Français: "fr-FR", English: "en-US", Español: "es-ES", Deutsch: "de-DE", Italiano: "it-IT", Português: "pt-PT", Nederlands: "nl-NL" };
const voiceLang = (lang, text) => VOICES[lang] || (/[àâéèêëîïôùûçœ]/i.test(text) ? "fr-FR" : "en-US");
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// Read-aloud helper to learn the poem by heart (browser voice, free).
export function ReciteBox({ summary, lang }) {
  const L = useL();
  const { title, stanzas } = useMemo(() => parsePoem(summary), [summary]);
  const [playing, setPlaying] = useState(null); // null | "all" | stanza index
  const [current, setCurrent] = useState(0);
  const [slow, setSlow] = useState(true);
  const run = useRef(0);
  const supported = typeof window !== "undefined" && "speechSynthesis" in window;

  const stop = () => {
    run.current += 1;
    if (supported) window.speechSynthesis.cancel();
    setPlaying(null);
  };
  useEffect(() => stop, [summary]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!supported || stanzas.length === 0) return null;

  const say = (text, id) =>
    new Promise((resolve) => {
      if (run.current !== id) return resolve();
      const u = new window.SpeechSynthesisUtterance(text);
      u.lang = voiceLang(lang, summary);
      u.rate = slow ? 0.78 : 0.95;
      u.onend = resolve;
      u.onerror = resolve;
      window.speechSynthesis.speak(u);
    });

  const readStanza = async (i, id) => {
    for (const verse of stanzas[i]) {
      if (run.current !== id) return;
      await say(verse, id);
      await wait(slow ? 450 : 250);
    }
  };

  const playAll = async () => {
    stop();
    const id = run.current;
    setPlaying("all");
    if (title) { await say(title, id); await wait(900); }
    for (let i = 0; i < stanzas.length; i++) {
      if (run.current !== id) return;
      setCurrent(i);
      await readStanza(i, id);
      await wait(slow ? 1500 : 900);
    }
    if (run.current === id) setPlaying(null);
  };

  const playOne = async (i) => {
    stop();
    const id = run.current;
    setCurrent(i);
    setPlaying(i);
    await readStanza(i, id);
    if (run.current === id) setPlaying(null);
  };

  const btn = "font-mono uppercase tracking-[0.14em] text-[11px] px-3 py-2 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition-colors flex items-center gap-1.5 disabled:opacity-40";
  return (
    <section className="max-w-3xl border border-[#002FA7] p-5" data-testid="recite-box">
      <div className="flex items-center gap-2 mb-3">
        <Headphones className="w-4 h-4 text-[#002FA7]" />
        <span className="font-mono uppercase tracking-[0.18em] text-[12px] font-semibold text-[#002FA7]">{L("Récitation à voix haute", "Read-aloud recitation")}</span>
        <InfoTip testid="recite-info" content={L(<><b>Récitation à voix haute</b> : écoutez le poème seul, lu lentement, avec une pause entre chaque strophe. Puis apprenez-le <b>strophe par strophe</b> : Répéter autant de fois que nécessaire, puis Suivante. Voix de votre appareil, gratuite (aucun crédit).</>, <><b>Read-aloud recitation</b>: listen to the poem alone, read slowly with a pause between stanzas. Then learn it <b>stanza by stanza</b>: Repeat as often as needed, then Next. Uses your device voice, free (no credit).</>)} />
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {playing === "all"
          ? <button type="button" className={btn} onClick={stop} data-testid="recite-stop-btn"><Square className="w-3 h-3" /> {L("Arrêter", "Stop")}</button>
          : <button type="button" className={btn} onClick={playAll} data-testid="recite-play-all-btn"><Play className="w-3 h-3" /> {L("Écouter le poème", "Listen to the poem")}</button>}
        <label className="flex items-center gap-2 text-xs text-muted-foreground ml-auto">
          <input type="checkbox" checked={slow} onChange={(e) => setSlow(e.target.checked)} data-testid="recite-slow-toggle" /> {L("Lecture lente", "Slow reading")}
        </label>
      </div>
      <div className="bg-muted/40 p-4 mb-3" data-testid="recite-current-stanza">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">{L("Strophe", "Stanza")} {current + 1} / {stanzas.length}</div>
        {stanzas[current].map((v, k) => <div key={k} className="leading-relaxed">{v}</div>)}
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" className={btn} disabled={current === 0} onClick={() => playOne(current - 1)} data-testid="recite-prev-btn"><SkipBack className="w-3 h-3" /> {L("Précédente", "Previous")}</button>
        <button type="button" className={btn} onClick={() => playOne(current)} data-testid="recite-repeat-btn"><Repeat className="w-3 h-3" /> {playing === current ? L("Lecture…", "Playing…") : L("Répéter", "Repeat")}</button>
        <button type="button" className={btn} disabled={current >= stanzas.length - 1} onClick={() => playOne(current + 1)} data-testid="recite-next-btn"><SkipForward className="w-3 h-3" /> {L("Suivante", "Next")}</button>
      </div>
    </section>
  );
}

export default ReciteBox;
